import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { logAppMemoryReport } from "../../tools/AppMemoryProfiler.tools";
import { persistentSliceStore } from "./runtime/PersistentSliceStore";
import { EAGER_PERSISTED_SLICES } from "./slices/registry";
import type { PersistenceBootStatus } from "./types";

/**
 * React context value exposed while persistence boots and after eager hydration.
 */
export type PersistenceContextValue = { "status": PersistenceBootStatus };

/** Context used by `usePersistenceStatus` / `usePersistenceReady`. */
export const PersistenceContext = createContext<PersistenceContextValue | null>( null );

/**
 * Root provider that hydrates eager persisted slices at application boot.
 *
 * @remarks
 * - Disables iCloud Keychain sync for secure storage (native).
 * - Sets context `status` to `ready` after `EAGER_PERSISTED_SLICES` finish hydrating.
 * - Lazy slices still load when hooks first subscribe.
 */
export const PersistenceProvider: React.FC<{ "children": React.ReactNode }> = ( { children } ) => {
    const [ status, setStatus ] = useState<PersistenceBootStatus>( "booting" );

    useEffect( () => {
        /**
         * Boots persistence: secure-storage tuning, then parallel eager hydration.
         */
        const boot = async(): Promise<void> => {
            try {
                await SecureStorage.setSynchronize( false );
            } catch( error: unknown ) {
                console.warn( "[PersistenceProvider] setSynchronize(false) failed:", error );
            }

            await persistentSliceStore.hydrateEagerSlices( EAGER_PERSISTED_SLICES );
            setStatus( "ready" );

            logAppMemoryReport( {
                "context":      "persistence-ready",
                "skipThrottle": true,
            } );
        };

        void boot();
    }, [] );

    const value = useMemo(
        (): PersistenceContextValue => ( { status } ),
        [ status ],
    );

    return (
        <PersistenceContext.Provider value={value}>
            {children}
        </PersistenceContext.Provider>
    );
};
